import React, { useEffect, useMemo, useState } from "react";

import displayToast from "@common/components/Toast/displayToast";
import { userFeedbackAttributes } from "@common/hooks/filtering/modules/useFeedbackAttributes";
import { Attachment, File as IssueFile, PlatformOs, SupportedFileTypes, Tag } from "@common/models";
import { FeedbackType } from "@common/models/FeedbackType";
import { Issue, IssueChatRequest } from "@common/models/issueTracking";
import { TicketPriority } from "@common/models/TicketPriority.model";
import { TicketStatus } from "@common/models/TicketStatus.model";
import { resolveFileType } from "@common/util/files";
import useCommonTagsApiConsumer from "@main/consumers/useCommonTagsApiConsumer";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { CreateIssueRequest } from "@main/pages/UserFeedback/services/CreateIssueRequest.model";
import { useUserFeedbackDeps } from "@main/pages/UserFeedback/UserFeedback";
import { BasicsItem } from "@main/pages/UserFeedback/views/UserFeedbackAddNewTicketView/BasicsItem/BasicsItem";
import { RoutePaths } from "@main/router/config/routePaths";
import { AxiosResponse } from "axios";
import { generatePath, useNavigate } from "react-router-dom";

const defaultTags: Tag[] = [{ name: "manually-created" }, { name: FeedbackType.BUG.toLowerCase() }];

interface UseUserFeedbackAddTicketViewProps {
  onTicketCreated?: (issue: Issue) => void;
  onDirtyChanged: (isDirty: boolean) => void;
}

export const useUserFeedbackAddNewTicketView = ({
  onTicketCreated,
  onDirtyChanged,
}: UseUserFeedbackAddTicketViewProps) => {
  const navigate = useNavigate();
  const { userFeedbackService } = useUserFeedbackDeps();

  const { selectedWorkspace, selectedApp } = useAppSelectionContext();
  const platform: PlatformOs = selectedApp?.platform?.os ?? PlatformOs.ANDROID;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [resetCount, setResetCount] = useState(0);

  const [title, setTitle] = useState<string>("");
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(FeedbackType.BUG);
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [status, setStatus] = useState<TicketStatus>(TicketStatus.NEW);
  const [priority, setPriority] = useState<TicketPriority>(TicketPriority.LOW);
  const [tags, setTags] = useState<Tag[]>(defaultTags);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [comment, setComment] = useState<string>("");
  const [basics, setBasics] = useState<BasicsItem[]>(getInitialBasics(platform));

  const [isFeedbackTypeDropdownOpened, setIsFeedbackTypeDropdownOpened] = useState(false);
  const [isStatusDropdownOpened, setIsStatusDropdownOpened] = useState(false);
  const [isPriorityDropdownOpened, setIsPriorityDropdownOpened] = useState(false);

  const { commonTags } = useCommonTagsApiConsumer({ selectedWorkspaceId: selectedWorkspace?.id });

  useEffect(() => {
    return () => attachments.forEach((att) => att.url && URL.revokeObjectURL(att.url));
  }, [attachments]);

  const isDirty = useMemo(() => {
    const initial = getInitialState(platform);
    return (
      title !== initial.title ||
      feedbackType !== initial.feedbackType ||
      assigneeId !== initial.assigneeId ||
      status !== initial.status ||
      priority !== initial.priority ||
      comment !== initial.comment ||
      JSON.stringify(tags) !== JSON.stringify(initial.tags) ||
      JSON.stringify(attachments) !== JSON.stringify(initial.attachments) ||
      JSON.stringify(basics) !== JSON.stringify(initial.basics)
    );
  }, [platform, title, feedbackType, assigneeId, status, priority, comment, tags, attachments, basics]);

  useEffect(() => {
    onDirtyChanged(isDirty);
  }, [isDirty, onDirtyChanged]);

  const createNewTicket = async () => {
    if (!selectedWorkspace?.id || !selectedApp?.id) return;

    try {
      setLoading(true);

      const files: File[] = attachments.map((a) => a.file).filter((f): f is File => Boolean(f));

      const uploadedFiles: IssueFile[] = [];
      for (const file of files) {
        const result: AxiosResponse<IssueFile> = await userFeedbackService.uploadUseFeedbackFile(
          file,
          selectedWorkspace?.id,
          selectedApp?.id,
        );
        uploadedFiles.push(result.data);
      }

      const createIssueRequest: CreateIssueRequest = {
        title: title ? title : "No description",
        priority: priority,
        status: status,
        assignee_id: assigneeId,
        os_name: basics.find((item) => item.key === userFeedbackAttributes.osName)?.value ?? "",
        os_version: basics.find((item) => item.key === userFeedbackAttributes.osVersion)?.value ?? "",
        device: basics.find((item) => item.key === userFeedbackAttributes.device)?.value ?? "",
        browser_name: basics.find((item) => item.key === userFeedbackAttributes.browserName)?.value ?? "",
        browser_version: basics.find((item) => item.key === userFeedbackAttributes.browserVersion)?.value ?? "",
        app_version: basics.find((item) => item.key === userFeedbackAttributes.appVersion)?.value ?? "",
        build_version: basics.find((item) => item.key === userFeedbackAttributes.appVersion)?.value ?? "",
        current_view: basics.find((item) => item.key === userFeedbackAttributes.currentView)?.value ?? "",
        files: uploadedFiles.filter((f) => Boolean(f.url)).map((f) => f.url ?? "") ?? [],
        tags: tags.map((t) => t.name),
      };

      const response: AxiosResponse<Issue> = await userFeedbackService.createUserFeedbackTicket(
        selectedWorkspace?.id,
        selectedApp?.id,
        createIssueRequest,
      );
      const createdIssue: Issue = response.data;

      if (comment) {
        const sendMessageRequest: IssueChatRequest = { message: comment, is_note: true };
        await userFeedbackService.sendUserFeedbackMessage(
          selectedWorkspace.id,
          selectedApp.id,
          createdIssue.id,
          sendMessageRequest,
        );
      }

      displayToast({
        title: "Ticket successfully created",
        content: <AddNewTicketToastContent link={createdIssue.permalink} />,
      });

      resetInput();

      onTicketCreated?.(createdIssue);
    } catch (e) {
      setError("Failed to create ticket. Please try again later.");
      displayToast({ title: "Something went wrong", content: "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const resetInput = () => {
    setTitle("");
    setFeedbackType(FeedbackType.BUG);
    setAssigneeId(null);
    setStatus(TicketStatus.NEW);
    setPriority(TicketPriority.LOW);
    setTags(defaultTags);
    setAttachments([]);
    setComment("");
    setBasics(getInitialBasics(platform));
    setResetCount((c) => c + 1);
  };

  const onBasicsValueChanged = (basicsItem: BasicsItem) => {
    const existingData: BasicsItem | undefined = basics.find((b) => b.key === basicsItem.key);
    if (existingData) {
      const updatedBasics: BasicsItem[] = basics.map((b) => (b.key === basicsItem.key ? basicsItem : b));
      setBasics(updatedBasics);
    }
  };

  const addAttachment = async (acceptedFile: File) => {
    let fileText: string | undefined;

    const type: SupportedFileTypes | undefined = resolveFileType(acceptedFile.name);
    if (type === SupportedFileTypes.TXT) fileText = await acceptedFile.text();

    const newAttachment: Attachment = {
      name: acceptedFile.name,
      url: URL.createObjectURL(acceptedFile),
      num: attachments.length,
      type: resolveFileType(acceptedFile.name),
      text: fileText,
      file: acceptedFile,
    };

    setAttachments([...attachments, newAttachment]);
  };

  const removeAttachment = (attachment: Attachment) => {
    const newAttachments: Attachment[] = attachments.filter((a) => a.url !== attachment.url);
    setAttachments(newAttachments);
  };

  const finalAttachments: Attachment[] = [
    ...attachments,
    {
      isSelectFile: true,
      num: attachments.length + 1,
    },
  ];

  const onFeedbackTypeSelected = (feedbackType: FeedbackType) => {
    setFeedbackType(feedbackType);

    const feedbackTypeTags: string[] = Object.values(FeedbackType).map((type) => type.toLowerCase());
    const newTags: Tag[] = tags.filter((tag) => !feedbackTypeTags.includes(tag.name.toLowerCase()));
    newTags.push({ name: feedbackType.toLowerCase() });
    setTags(newTags);
  };

  const setCommonComment = (commonComment: string) => {
    comment ? setComment(comment + "\n" + commonComment) : setComment(commonComment);
  };

  const showCommonCommentsSettings = () => {
    if (selectedWorkspace)
      navigate(
        generatePath(RoutePaths.ADMINISTRATION_COMMON_COMMENTS, {
          workspaceSlug: selectedWorkspace?.slug,
        }),
        { replace: true },
      );
  };

  return {
    title,
    setTitle,
    feedbackType,
    setFeedbackType,
    assigneeId,
    setAssigneeId,
    status,
    setStatus,
    priority,
    setPriority,
    tags,
    setTags,
    attachments: finalAttachments,
    setAttachments,
    comment,
    setComment,
    setCommonComment,
    basics,
    setBasics,
    isFeedbackTypeDropdownOpened,
    setIsFeedbackTypeDropdownOpened,
    isStatusDropdownOpened,
    setIsStatusDropdownOpened,
    isPriorityDropdownOpened,
    setIsPriorityDropdownOpened,
    onBasicsValueChanged,
    addAttachment,
    removeAttachment,
    onFeedbackTypeSelected,
    commonTags,
    createNewTicket,
    loading,
    error,
    showCommonCommentsSettings,
    resetCount,
  };
};

const getInitialState = (platform: PlatformOs) => {
  return {
    title: "",
    feedbackType: FeedbackType.BUG,
    assigneeId: null,
    status: TicketStatus.NEW,
    priority: TicketPriority.LOW,
    tags: defaultTags,
    attachments: [],
    files: [],
    comment: "",
    basics: getInitialBasics(platform),
  };
};

interface AddNewTicketToastProps {
  link: string;
}

const AddNewTicketToastContent = ({ link }: AddNewTicketToastProps) => {
  const [isCopied, setIsCopied] = useState(false);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {"Share this new ticket with your team."}
      <div style={{ display: "flex", flexDirection: "row", marginTop: "0.8rem" }}>
        <a
          onClick={() => {
            setIsCopied(true);
            navigator.clipboard.writeText(link);
            setTimeout(() => setIsCopied(false), 3000);
          }}
        >
          {isCopied ? "Copied" : "Copy link"}
        </a>
        &nbsp;&nbsp;&nbsp;•&nbsp;&nbsp;&nbsp;
        <a onClick={() => window.open(link, "_blank")?.focus()}>View ticket</a>
      </div>
    </div>
  );
};

const getInitialBasics = (platform: PlatformOs): BasicsItem[] => {
  if (platform === PlatformOs.WEB) {
    return [
      {
        key: userFeedbackAttributes.currentView,
        label: "Current URL",
        value: "",
      },
      {
        key: userFeedbackAttributes.osName,
        label: "OS name",
        value: "",
      },
      { key: userFeedbackAttributes.osVersion, label: "OS version", value: "" },
      { key: userFeedbackAttributes.browserName, label: "Browser name", value: "" },
      { key: userFeedbackAttributes.browserVersion, label: "Browser version", value: "" },
    ];
  }
  if (platform === PlatformOs.ANDROID) {
    return [
      { key: "os_android", label: "OS name", value: "Android" },
      { key: userFeedbackAttributes.osVersion, label: "OS version", value: "" },
      { key: userFeedbackAttributes.device, label: "Device", value: "" },
      { key: userFeedbackAttributes.appVersion, label: "App version", value: "" },
      { key: userFeedbackAttributes.buildNumber, label: "Build number", value: "" },
    ];
  }
  if (platform === PlatformOs.IOS) {
    return [
      { key: "os_ios", label: "OS name", value: "iOS" },
      { key: userFeedbackAttributes.osVersion, label: "OS version", value: "" },
      { key: userFeedbackAttributes.device, label: "Device", value: "" },
      { key: userFeedbackAttributes.appVersion, label: "App version", value: "" },
      { key: userFeedbackAttributes.buildNumber, label: "Build number", value: "" },
    ];
  }
  return [];
};
